import React from 'react';
import { Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThLarge, faUsers } from '@fortawesome/free-solid-svg-icons';
import { useHistory } from 'react-router-dom';
import { formatDateAndTime, formatDateAndTimeFromNow } from '../../../helpers/utils';
import { getTagForRuntimeStates } from '../../Common/Tags';
import { CounterpartyName, PartyField } from '../../Teams/Counterparty';
import { ChangeState } from './ServiceItemRow';

/**
 * Grid tile. Same information and design tokens as the wide row, laid out
 * vertically: identity on top, state in the middle, ownership pinned to a
 * footer so tiles line up regardless of how long the names are.
 */
const ServiceItemCard = ({
    record,
    showChangeState,
    clickable,
    detailPath,
    changeInstancesPath,
    partyField,
}: {
    record: any;
    showChangeState: boolean;
    clickable: boolean;
    detailPath: string;
    changeInstancesPath: string;
    partyField: PartyField;
}) => {
    const history = useHistory();
    const open = () => clickable && history.push(`${detailPath}/${record.id}`);
    const party = partyField.team(record);

    return (
        <div
            className={`si-card${clickable ? '' : ' si-card--static'}`}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-label={clickable ? `Open service item ${record.name}` : undefined}
            onClick={open}
            onKeyDown={(e) => {
                if (clickable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    open();
                }
            }}
        >
            <div className="si-card__head">
                <Tooltip placement="topLeft" title={record.name}>
                    <span className="si-card__name">{record.name}</span>
                </Tooltip>
                <Tooltip placement="topLeft" title={record.service?.name}>
                    <span className="si-card__service">{record.service?.name || '—'}</span>
                </Tooltip>
            </div>

            <div className="si-card__state">
                {getTagForRuntimeStates(record.runtime_state)}
                {showChangeState && <ChangeState record={record} changeInstancesPath={changeInstancesPath} />}
            </div>

            <div className="si-card__foot">
                <div className="si-card__fact">
                    <span className="si-card__ico" aria-hidden="true"><FontAwesomeIcon icon={faThLarge} /></span>
                    <Tooltip placement="topLeft" title={record.application?.name}>
                        <span>{record.application?.name || '—'}</span>
                    </Tooltip>
                </div>
                <div className="si-card__fact">
                    <span className="si-card__ico" aria-hidden="true"><FontAwesomeIcon icon={faUsers} /></span>
                    <Tooltip placement="topLeft" title={`${partyField.label}: ${party?.name || '—'}`}>
                        <span>
                            <CounterpartyName team={party} pov={partyField.pov} label={partyField.label} />
                        </span>
                    </Tooltip>
                </div>
                <div className="si-card__meta">
                    #{record.id}
                    {record.created && (
                        <Tooltip title={formatDateAndTime(record.created)}>
                            {' · '}{formatDateAndTimeFromNow(record.created)}
                        </Tooltip>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ServiceItemCard;
